"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/shared/AccountCategoryCards";

import React from "react";
import { cn } from "utils";
import type { PlaidAccountCategory } from "../types";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 账户类别双卡（投资 / 银行）。
 * 抽取自 PlaidCategorySelectStep（"How would you like to connect?" 下方的卡片组），
 * 聊天 request_portfolio 交互态复用；卡片视觉与原弹窗内保持一致。
 */
export interface AccountCategoryCardsProps {
  onSelectCategory: (category: PlaidAccountCategory) => void;
  /** 禁用整组卡片（如聊天中非最后一条消息且无 result） */
  disabled?: boolean;
  className?: string;
  /**
   * 只渲染指定一侧；**不传 = 双卡（存量行为，勿改默认）**。
   * 映射自 `request_portfolio` 的 `intentSide`：invest→`invest`、banking→`bank`、
   * both/缺省→不传（见 `ChatOnboarding/requestPortfolioIntentSide.ts`）。
   */
  only?: PlaidAccountCategory;
}

const INVEST_LOGOS = [
  {
    name: "Fidelity",
    src: "/assets/images/plaid/category/invest-fidelity.webp",
  },
  {
    name: "Charles Schwab",
    src: "/assets/images/plaid/category/invest-schwab.webp",
  },
  {
    name: "Vanguard",
    src: "/assets/images/plaid/category/invest-vanguard.webp",
  },
  {
    name: "Robinhood",
    src: "/assets/images/plaid/category/invest-robinhood.webp",
  },
];

const BANK_LOGOS = [
  { name: "Chase", src: "/assets/images/plaid/category/bank-chase.webp" },
  {
    name: "Capital One",
    src: "/assets/images/plaid/category/bank-capitalone.webp",
  },
  {
    name: "Wells Fargo",
    src: "/assets/images/plaid/category/bank-wellsfargo.webp",
  },
  {
    name: "Bank of America",
    src: "/assets/images/plaid/category/bank-bofa.webp",
  },
];

/**
 * 卡片文案（与 PlaidCategorySelectStep 原文案一致）；查看态 action 映射复用同一来源。
 * `~...~` 为 i18n 扫描标记（review #7）：`pnpm run scan` 会把值改写为 `I18n.t("…")`
 * 并生成本文件专属 translate 模块 —— Chat 侧 `parseRequestPortfolioResult` 复用同一常量，
 * 翻译落位后自动跟随。
 */
export const ACCOUNT_CATEGORY_CARD_COPY = {
  invest: {
    title: I18n.t("Connect investment account"),
    description: I18n.t("Brokerages, retirement, robo-advisors"),
  },
  bank: {
    title: I18n.t("Connect banking account"),
    description: I18n.t("Checking, savings, credit cards"),
  },
} as const;

interface AccountCategoryLogoRowProps {
  logos: typeof INVEST_LOGOS;
}

const AccountCategoryLogoRow: React.FC<AccountCategoryLogoRowProps> = ({
  logos,
}) => (
  <div className="flex items-center">
    {logos.map((logo, index) => (
      <div
        key={logo.name}
        className={`size-9 rounded-full overflow-hidden border-[1.6px] border-border_solid relative bg-background_solid_tertiary shrink-0 ${
          index < logos.length - 1 ? "mr-[-9px]" : ""
        }`}
      >
        <img
          src={logo.src}
          alt={logo.name}
          className="w-full h-full object-cover pointer-events-none"
        />
      </div>
    ))}
  </div>
);

interface AccountCategoryCardProps {
  category: PlaidAccountCategory;
  logos: typeof INVEST_LOGOS;
  disabled?: boolean;
  onSelect: (category: PlaidAccountCategory) => void;
}

const AccountCategoryCard: React.FC<AccountCategoryCardProps> = ({
  category,
  logos,
  disabled,
  onSelect,
}) => {
  const copy = ACCOUNT_CATEGORY_CARD_COPY[category];

  return (
    <button
      type="button"
      onClick={() => onSelect(category)}
      disabled={disabled}
      data-testid={`account-category-card-${category}`}
      data-openreplay-label={
        category === "invest"
          ? "plaidconnectflow_categoryselect_invest"
          : "plaidconnectflow_categoryselect_bank"
      }
      className={cn(
        "w-full bg-background_solid_primary rounded-xl px-4 py-5 flex flex-col gap-4 items-start text-left border border-border_solid/40 active:scale-[0.99] transition-all cursor-pointer shadow-sm group disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100",
        // hover 高亮仅在可用态施加：disabled 按钮的 :hover 依然触发（触摸端点击后粘住），
        // 会导致禁用卡出现品牌高亮边框
        !disabled && "hover:border-border_brand_solid/60",
      )}
    >
      <AccountCategoryLogoRow logos={logos} />
      <div className="flex flex-col gap-0.5">
        <span
          className={cn(
            "text-base font-semibold text-content_primary leading-6 transition-colors",
            !disabled && "group-hover:text-content_brand_primary",
          )}
        >
          {copy.title}
        </span>
        <span className="text-xs text-content_tertiary leading-4">
          {copy.description}
        </span>
      </div>
    </button>
  );
};

const CATEGORY_LOGOS: Record<PlaidAccountCategory, typeof INVEST_LOGOS> = {
  invest: INVEST_LOGOS,
  bank: BANK_LOGOS,
};

/** 渲染顺序即现状顺序（invest 在前）；`only` 只做过滤，不改序。 */
const ALL_CATEGORIES: PlaidAccountCategory[] = ["invest", "bank"];

/** `only` 命中即单卡，否则双卡（存量行为）。 */
export function visibleAccountCategories(
  only?: PlaidAccountCategory,
): PlaidAccountCategory[] {
  return only ? ALL_CATEGORIES.filter((c) => c === only) : ALL_CATEGORIES;
}

export const AccountCategoryCards: React.FC<AccountCategoryCardsProps> = ({
  onSelectCategory,
  disabled,
  className,
  only,
}) => (
  <div className={`flex flex-col gap-3 w-full ${className ?? ""}`}>
    {visibleAccountCategories(only).map((category) => (
      <AccountCategoryCard
        key={category}
        category={category}
        logos={CATEGORY_LOGOS[category]}
        disabled={disabled}
        onSelect={onSelectCategory}
      />
    ))}
  </div>
);
